import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import { useAuth } from '../../contexts/AuthContext';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    password: '',
    confirmPassword: '',
    token: ''
  });
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setErrors(prev => ({
        ...prev,
        token: 'Invalid reset link. Please request a new password reset.'
      }));
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const validatePassword = (password: string): boolean => {
    // At least 8 characters, one letter, one number
    const hasMinLength = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    return hasMinLength && hasLetter && hasNumber;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setErrors(prev => ({
        ...prev,
        token: 'Invalid reset link. Please request a new password reset.'
      }));
      return;
    }

    const newErrors = {
      password: '',
      confirmPassword: '',
      token: ''
    };

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long with at least one letter and one number.';
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password.';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    setErrors(newErrors);

    // If no errors, proceed with password reset
    if (!newErrors.password && !newErrors.confirmPassword && !newErrors.token) {
      setIsLoading(true);
      
      try {
        const response = await fetch('/api/v1/auth/reset-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            password: formData.password,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Login the user using the auth context
          login(data.accessToken, data.user, data.refreshToken);
          // Redirect to the daily page (ListDaily)
          navigate('/daily');
        } else {
          // Handle reset errors
          if (data.message && data.message.includes('Invalid or expired')) {
            setErrors(prev => ({
              ...prev,
              token: 'Invalid or expired reset link. Please request a new password reset.'
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              password: data.message || 'Password reset failed. Please try again.'
            }));
          }
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          password: 'Network error. Please check your connection and try again.'
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      <Header showSignUp={true} showLogIn={false} />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-text-light-primary dark:text-text-primary">
              Reset your password
            </h2>
            <p className="mt-2 text-center text-sm text-text-light-secondary dark:text-text-secondary">
              Enter your new password below.
            </p>
          </div>
          
          <div className="bg-surface-light dark:bg-background-card p-8 rounded-lg shadow-lg border border-border-light dark:border-border-color">
            {errors.token && (
              <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.token}</p>
                <Link 
                  to="/login" 
                  className="text-sm text-red-600 dark:text-red-400 underline mt-2 inline-block"
                >
                  Go back to login
                </Link>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div>
                <label className="block text-sm font-medium text-text-light-secondary dark:text-text-secondary" htmlFor="password">
                  New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    autoComplete="new-password"
                    className={`w-full p-3 pr-12 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      errors.password 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="password"
                    name="password"
                    required
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 px-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4L20 20"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-light-secondary dark:text-text-secondary" htmlFor="confirmPassword">
                  Confirm New Password
                </label>
                <div className="mt-1 relative">
                  <input
                    autoComplete="new-password"
                    className={`w-full p-3 pr-12 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      errors.confirmPassword 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="confirmPassword"
                    name="confirmPassword"
                    required
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 px-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4L20 20"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
              
              <div>
                <button
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={isLoading || !token}
                >
                  {isLoading ? 'Resetting password...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
          
          <p className="mt-10 text-center text-xs text-text-light-secondary dark:text-text-secondary">
            Remember your password?{' '}
            <Link className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors" to="/login">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;

